import { Cameron, Filter } from "@components/icons"
import MenuContext from "@components/Menu/MenuContext"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { smallcapsRegular13Styles } from "@constants/typography"
import { getIn } from "@utils/index"
import React from "react"
import styled, { css } from "styled-components"

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 120px;
  padding: 0 20px;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.white};

  @media ${breakpoints.tablet} {
    padding: 0 calc(6 / 9 / 12 * 100vw - 20px) 0 calc(6 / 9 / 12 * 100vw);
  }
`

const Breadcrumb = styled.button<{ hidden?: boolean }>`
  display: flex;
  height: 40px;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0 16px 0 2px;
  background: none;
  border: solid 1px ${colors.dividerLineGray};
  border-radius: 2px;
  outline: none;
  ${({ hidden }) => hidden && "visibility: hidden;"}

  &:active {
    outline: none;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    cursor: pointer;
  }
`

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  transform: rotate(180deg);
`

const BreadcrumbText = styled.span`
  ${smallcapsRegular13Styles}
  display: flex;
  margin-left: 2px;
  padding-top: 3px;
  font-size: 11px;
  color: ${colors.mediumDarkGray};
  line-height: 19px;
  letter-spacing: 1px;
`

const FilterButton = styled.button<{
  isAtLeastOneFilter: boolean
  isFullWidth?: boolean
}>`
  ${({ isAtLeastOneFilter, isFullWidth = false }) => css`
    display: flex;
    position: relative;
    min-width: ${isFullWidth ? "100%" : "64px"};
    height: 40px;
    justify-content: space-between;
    align-items: center;
    background-color: ${isAtLeastOneFilter ? colors.black : "transparent"};
    border-radius: 2px;
    cursor: pointer;
    border: 1px solid ${colors.dividerLineGray};
    padding: 0 14px;
    opacity: 0.9;
    color: ${isAtLeastOneFilter ? "#FEFDFC" : colors.mediumDarkGray};
    &:focus {
      outline: none;
    }
  `}
`

const FilterTextWrapper = styled.span`
  font-size: 11px;
  line-height: 19px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 400;
`

const FilterIconContainer = styled.div`
  height: 20px;
  width: 20px;
  margin-left: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const BubbleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${colors.black};
  background-color: ${colors.white};
  height: 16px;
  width: 16px;
  border-radius: 50%;
  font-size: 9px;
  line-height: 9px;
  font-weight: 700;
`

interface FilterBarProps {
  filterCount: number
  filters: {
    brand?: {
      name: string
      slug: string
    }
    collection?: {
      name: string
      slug: string
    }
    current: string
    device: {
      name: string
      slug: string
    }
    deviceType?: {
      menuLabel?: string
      name: string
      slug: string
    }
    previous: {
      brand?: {
        name: string
        slug: string
      }
      deviceType?: {
        name: string
        slug: string
      }
      name: string
      slug: string
    }
    series?: {
      name: string
      slug: string
    }
  }
  isClearance: boolean
  isCustomPrints: boolean
  onFilterClick: (event) => void
}

const FilterBar: React.FC<FilterBarProps> = ({
  filterCount,
  filters,
  isClearance,
  isCustomPrints,
  onFilterClick,
}: FilterBarProps) => {
  const { openMenu }: any = React.useContext(MenuContext)

  const handleClickBreadcrumb = (e) => {
    e.stopPropagation()
    if (!openMenu) {
      return
    }

    const { previous } = filters

    if (previous && previous.deviceType && previous.brand) {
      openMenu({
        brand: previous.brand.slug,
        deviceType: previous.deviceType.slug,
      })
    } else if (previous && previous.deviceType) {
      openMenu({
        deviceType: previous.deviceType.slug,
      })
    } else if (previous) {
      openMenu({})
    }
  }

  const handleClickFilter = (event) => {
    if (!openMenu) {
      return
    }

    const { brand, collection, device, deviceType, series } = filters

    if (
      isCustomPrints ||
      isClearance ||
      collection ||
      series ||
      (deviceType && brand && device)
    ) {
      onFilterClick(event)
    } else if (deviceType && brand) {
      openMenu({
        brand: brand.slug,
        deviceType: deviceType.slug,
      })
    } else if (deviceType) {
      openMenu({
        deviceType: deviceType.slug,
      })
    } else {
      openMenu({})
    }
  }

  const filterName = React.useMemo(() => {
    if (
      isCustomPrints ||
      filters.collection ||
      (filters.previous?.brand && filters.previous?.deviceType)
    ) {
      return "Filter"
    } else if (filters.previous?.deviceType) {
      return "Model"
    } else if (filters.previous) {
      return "Brand"
    }
    return "Shop By Device"
  }, [filters.previous])

  return (
    <Container>
      {!!filters.previous && (
        <Breadcrumb hidden={!filters.previous} onClick={handleClickBreadcrumb}>
          <IconWrapper>
            <Cameron strokeColor={colors.mediumDarkGray} />
          </IconWrapper>
          <BreadcrumbText>{getIn(filters, "previous.name", "")}</BreadcrumbText>
        </Breadcrumb>
      )}
      <FilterButton
        id="mobileCollectionsFilterButton"
        isAtLeastOneFilter={filterCount > 0}
        isFullWidth={!filters.previous}
        onClick={handleClickFilter}
      >
        <FilterTextWrapper>{filterName}</FilterTextWrapper>
        <FilterIconContainer>
          {filterCount > 0 ? (
            <BubbleContainer>{filterCount}</BubbleContainer>
          ) : (
            <Filter />
          )}
        </FilterIconContainer>
      </FilterButton>
    </Container>
  )
}

export default FilterBar
